import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { SellerEntitySchema, type SellerEntity } from '@etiquetachef/types';
import { firestore } from './firebase';
import { reportNonFatal } from './errorReport';

const TAG = 'admin/sellerEntities';

/** Estado da lista de entidades vendedoras. */
export type SellerEntityListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; entities: SellerEntity[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina `/config/sellerEntities/items/` (todas as entidades vendedoras).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7: o admin edita a `etiquetachef-br`).
 *
 * Contrato: entrega `READY` com as entidades válidas por `SellerEntitySchema`,
 * ordenadas por id; doc inválido é ignorado com report non-fatal
 * (`admin_seller_entity_invalid`) — sem ele, a entidade some da tela em
 * silêncio. Rules: só superAdmin lê; escrita só por `adminUpsertSellerEntity`.
 *
 * Usado por: `features/sellerEntities/SellerEntitiesPage.tsx`,
 * `features/sellerEntities/SellerEntityEditorPage.tsx` e
 * `features/billing/MarketPspFields.tsx` (seletor de entidade, dentro do
 * `MarketConfigEditDialog`).
 *
 * Arquitetura: rule `match /config/sellerEntities/items/{id}`.
 *
 * Dados: `config/sellerEntities/items/{id}` — schema `SellerEntitySchema`
 * (o `id` vem do corpo do doc, não do caminho).
 */
export function subscribeSellerEntities(onResult: (r: SellerEntityListResult) => void): Unsubscribe {
  const ref = collection(firestore, 'config', 'sellerEntities', 'items');
  return onSnapshot(
    ref,
    (snap) => {
      const entities: SellerEntity[] = [];
      for (const d of snap.docs) {
        const parsed = SellerEntitySchema.safeParse(d.data());
        if (parsed.success) entities.push(parsed.data);
        else {
          console.warn(`${TAG}: doc ${d.id} ignorado`, parsed.error.issues);
          reportNonFatal('admin_seller_entity_invalid', {
            id: d.id,
            issues: parsed.error.issues.slice(0, 5).map((i) => `${i.path.join('.')}: ${i.message}`),
          });
        }
      }
      entities.sort((a, b) => a.id.localeCompare(b.id));
      onResult({ kind: 'READY', entities });
    },
    (err) => {
      console.error(`${TAG}: erro`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
